import { ref } from "vue";
import type { CustomSignatureStatus } from "@/types/signature";
import { tryCatch } from "@/types/utils";
import { Connection } from "@solana/web3.js";

interface SignatureInfo {
  signature: string;
  listeners: ((status: CustomSignatureStatus) => void)[];
  status: CustomSignatureStatus[];
  createdAt: number;
}

function useSignatureStore() {
  // State
  const signatures = ref<Record<string, SignatureInfo>>({});
  const intervalId = ref<ReturnType<typeof setInterval>>();
  const UPDATE_INTERVAL = 10; // Poll every 10ms
  const connection = ref<Connection>();
  const processingSignatures = ref<Set<string>>(new Set()); // Track signatures being fetched

  // Methods
  function init(connectionInstance: Connection) {
    connection.value = connectionInstance;
  }
  // Subscribe to signature status updates
  function listen(
    signature: string,
    listener: (status: CustomSignatureStatus) => void,
  ) {
    if (signatures.value[signature]) {
      signatures.value[signature].listeners.push(listener);
      return;
    }

    signatures.value[signature] = {
      createdAt: Date.now(),
      signature,
      listeners: [listener],
      status: [],
    };
  }
  // Unsubscribe from signature updates
  function unlisten(signature: string) {
    delete signatures.value[signature];
    processingSignatures.value.delete(signature);
  }
  // Notify all listeners for a signature
  function execListeners(signature: string, status: CustomSignatureStatus) {
    const sig = signatures.value[signature];
    if (!sig) {
      return;
    }

    sig.status.push(status);

    for (const listener of sig.listeners) {
      listener(status);
    }
  }
  // Fetch status updates for all tracked signatures
  async function getUpdates() {
    const allSigs = Object.keys(signatures.value);
    const allSigsLen = allSigs.length;

    if (allSigsLen === 0) {
      return;
    }

    // Filter out signatures already being processed (prevents duplicate requests)
    const sigs: string[] = [];
    const processing = processingSignatures.value;

    for (let i = 0; i < allSigsLen; i++) {
      const sig = allSigs[i];
      if (!processing.has(sig)) {
        sigs.push(sig);
        processing.add(sig);
      }
    }

    const sigsLen = sigs.length;
    if (sigsLen === 0) {
      return;
    }

    const { ok, error } = await tryCatch(async () => {
      const statuses = await connection.value?.getSignatureStatuses(sigs, {
        searchTransactionHistory: false,
      });

      if (!statuses?.value) {
        return { ok: true, data: null };
      }

      const statusValues = statuses.value;
      const now = Date.now();

      // Process each signature status
      for (let i = 0; i < sigsLen; i++) {
        const sig = sigs[i];
        const status = statusValues[i];

        if (!status || !signatures.value[sig]) {
          processing.delete(sig);
          continue;
        }

        const customStatus = {
          ...status,
          timestamp: now,
          status: status?.confirmationStatus,
        } as CustomSignatureStatus;

        // Check if we should stop listening (error, timeout, or confirmed)
        const gotError = status?.err;
        const gotTimeout = now - signatures.value[sig].createdAt > 60_000;
        const isFinalized = status?.confirmationStatus === "finalized";
        const isConfirmed = status?.confirmationStatus === "confirmed";
        const stopListen = gotError || gotTimeout || isFinalized || isConfirmed;

        if (gotError) {
          customStatus.status = "error";
        }
        if (gotTimeout) {
          customStatus.status = "timeout";
        }

        execListeners(sig, customStatus);

        if (stopListen) {
          unlisten(sig);
        }

        processing.delete(sig);
      }
      return { ok: true, data: null };
    });

    // Clean up processing state on error
    for (let i = 0; i < sigsLen; i++) {
      processing.delete(sigs[i]);
    }

    if (!ok) {
      console.error("Failed to fetch signature statuses:", error);
    }
  }

  function startAutoUpdate(interval = UPDATE_INTERVAL) {
    if (!intervalId.value) {
      intervalId.value = setInterval(getUpdates, interval);
    }
  }

  function stopAutoUpdate() {
    if (intervalId.value) {
      clearInterval(intervalId.value);
      intervalId.value = undefined;
    }
  }

  // Auto-start polling
  startAutoUpdate();

  return {
    init,
    listen,
    unlisten,
    execListeners,
    getUpdates,
    signatures,
    stopAutoUpdate,
  };
}

export const signatureStore = useSignatureStore();
